import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { calculateReturnedPalletsPerOrder } from "../utils/calculateReturnedPallets"
import { getFilters } from "../utils/orderHelpers"

export const queryCustomerOrders = {
  async customerOrders(_parent, args, context, _info) {
    let { customerNumber, page, size, sort, filterValues, orderText, searchText } = args
    const { user } = context

    let filters = getFilters(filterValues, searchText, orderText)

    await checkPermissions({
      user,
      domainObject: DomainObjects.ORDERS,
      operation: Operations.READ,
    })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customerNumber,
    })

    const hasFilter = filters && filters.length > 0
    const method = hasFilter ? "orders: findOrdersByCustomerAndFilter" : "orders: findOrdersByCustomer"

    const hasAddressFilter = (filter) => filter.field?.includes("address")

    if (filters?.some(hasAddressFilter)) {
      const customer = await getCustomer({
        number: customerNumber,
      })

      // remove the address filter again as it is not working on the graphql query
      const addressFilter = filters.filter((item) => hasAddressFilter(item))
      filters = filters.filter((item) => !hasAddressFilter(item))

      if (customer?.addresses?.length > 0) {
        const customerAddresses = customer.addresses || []
        const shippingAddressIds = customer?.shippingAddressIds || []

        // filtered shipping addresses
        let shippingAddresses = customerAddresses.filter((address) => shippingAddressIds.includes(address.id))

        shippingAddresses = shippingAddresses.filter((address) => address.id === addressFilter[0].value)

        if (shippingAddresses.length > 0) {
          const addressFilters = [
            shippingAddresses[0].postalCode && {
              field: "shippingPostalCode",
              operator: "eq",
              value: shippingAddresses[0].postalCode.trim(),
            },
            shippingAddresses[0].streetName && {
              field: "shippingStreet",
              operator: "eq",
              value: shippingAddresses[0].streetName.trim(),
            },
            shippingAddresses[0].city && {
              field: "shippingCity",
              operator: "eq",
              value: shippingAddresses[0].city.trim(),
            },
          ].filter((filter) => filter)

          filters = filters.concat(addressFilters)
        }
      }
    }

    const graphiQLQueryObject = [
      {
        method,
        variables: {
          customerNumber,
          ...(hasFilter && { filters }),
          page,
          size,
          ...(sort && { sort: EinzaGraphiQLService.formatSortParam(sort) }),
        },
        fields: ["number", "numberOfElements", "totalElements", "totalPages"],
        expandedFields: {
          content: [
            "orderNumberLong: orderNumber",
            "orderNumberShort: orderNumber2",
            "customerNumber",
            "paymentMethod",
            "orderType",
            "orderDate",
            "admNumber",
            "totalAmount",
            "branchNumber",
            "customerBranchNumber",
            "orderStatus",
            "approvalDate",
            "customerOrderText",
            "picklist",
          ],
        },
      },
    ]

    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const customerOrdersResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.orders

    if (!customerOrdersResponse?.content) {
      console.error("customerOrdersResponse - No response available")
      return null
    }

    // Calculate returned pallet amounts for each order
    // Get return orders (RÜ and BR) with pallets for the current year
    const currentYear = new Date().getFullYear().toString()
    const returnedFilters = [
      { field: "orderType", operator: "eq", value: "RÜ" },
      { field: "orderType", operator: "eq", value: "BR" },
    ]

    const generalFilters = [
      { field: "year", operator: "eq", value: currentYear },
      { field: "item.sku", operator: "eq", value: "0040210" },
      { field: "orderStatus", operator: "eq", value: "F" },
    ]

    let returnOrderQueries = []
    for (const filter of returnedFilters) {
      const filters = [...generalFilters, filter]
      const graphiQLQueryObject = [
        {
          method: "orders: findOrdersByCustomerAndFilter",
          variables: {
            customerNumber,
            filters,
            page: 0,
            size: 500,
          },
          fields: ["number", "numberOfElements", "totalElements", "totalPages"],
          expandedFields: {
            content: [
              "items { sku, amount, additionalDescription: description2 }",
              "orderNumber",
              "orderNumber2",
              "orderType",
            ],
          },
        },
      ]
      const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
      returnOrderQueries.push(EinzaGraphiQLService.getData(mappedQuery))
    }

    const resolvedReturnQueries = await Promise.all(returnOrderQueries)
    const returnOrders = resolvedReturnQueries.reduce((acc, res) => {
      const content = res?.data?.orders?.content || []
      return acc.concat(content)
    }, [])

    const returnedPalletsMap = calculateReturnedPalletsPerOrder(returnOrders)

    // Add returnedPalletAmount to each order
    const itemsWithReturnedAmounts = customerOrdersResponse.content.map((order) => {
      // Match against "orderType-orderNumberShort" format (e.g., "AU-2138277")
      const orderKey = order.orderType && order.orderNumberShort ? `${order.orderType}-${order.orderNumberShort}` : null
      const returnedAmount = orderKey ? returnedPalletsMap.get(orderKey) || 0 : 0
      return {
        ...order,
        returnedPalletAmount: returnedAmount,
      }
    })

    return {
      items: itemsWithReturnedAmounts,
      page,
      size,
      total: customerOrdersResponse.totalElements,
      totalPages: customerOrdersResponse.totalPages,
    }
  },
}
